import { useState } from "react";
import { motion } from "framer-motion";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useAccount } from "wagmi";
import { formatEther } from "viem";
import {
    Sparkles, Minus, Plus, Loader2, CheckCircle2, AlertTriangle, ArrowUpRight,
} from "lucide-react";
import { useMintInfo, useMint, useMyNFTs, useReveal } from "@/hooks/useMint";
import { ADDRESSES, CHAIN_ID, EXPLORER_TX, marketplaceCollectionUrls } from "@/lib/config";
import { useFireOnConfirm } from "@/hooks/useFireOnConfirm";
import { RARITY_LABEL, RARITY_COLOR, RARITY_BOOST_BPS } from "@/lib/abis";
import { PiggyMark, EthMark } from "@/components/Logo";
import { CountUp } from "@/components/CountUp";

export default function Mint() {
    const { isConnected, chainId } = useAccount();
    const wrongChain = isConnected && chainId !== CHAIN_ID;
    const noContract = !ADDRESSES.PIGGY_ORIGINALS;

    const { info, live, refetch } = useMintInfo();
    const [quantity, setQuantity] = useState(1n);

    const maxAllowed = info.perWalletCap - info.mintedByMe;
    const supplyLeft = info.maxSupply - info.totalMinted;
    const effectiveMax = maxAllowed < supplyLeft ? maxAllowed : supplyLeft;
    const clampedQty = quantity > effectiveMax ? effectiveMax : (quantity < 1n ? 1n : quantity);

    const { mint, status, hash } = useMint(clampedQty, info.mintPrice);
    const busy = status === "pending" || status === "confirming";
    const justMinted = status === "confirmed";

    useFireOnConfirm(justMinted, refetch);

    return (
        <section className="max-w-6xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <Sparkles className="w-3 h-3" />
                    Genesis Edition — 5,000 forever
                </div>
                <h1 className="display text-5xl md:text-6xl font-black tracking-tighter mb-4">
                    Mint a <span className="gradient-text-shimmer">Piggy</span>
                </h1>
                <p className="text-piggy-cream/60 text-lg max-w-2xl mx-auto">
                    6 rarities, committed on-chain at deploy via a Merkle root —
                    so the rarity you reveal is provably the one assigned to your
                    token, with no dev pre-pick. Every NFT boosts your LP staking
                    multiplier on top of your lockup tier — a feather in your cap
                    that actually pays.
                </p>
            </motion.div>

            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-8">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="glass rounded-3xl p-8"
                >
                    <div className="aspect-square max-w-md mx-auto rounded-3xl bg-gradient-to-br from-piggy-pink-400 via-piggy-pink-600 to-piggy-violet shadow-glow grid place-items-center mb-6 relative overflow-hidden">
                        <div className="absolute inset-0 bg-piggy-radial opacity-60" />
                        <div className="relative z-10 animate-float-slow">
                            <img
                                src="/piggy-classic.png"
                                alt="Safe Piggy mascot"
                                width={260}
                                height={260}
                                className="drop-shadow-[0_0_45px_rgba(204,255,0,0.35)]"
                                draggable={false}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-2">
                        <Stat label="Mint Price" value={<>{formatEther(info.mintPrice)} <EthMark size={14} /> <span>ETH</span></>} />
                        <Stat label="Your wallet cap" value={`${info.mintedByMe}/${info.perWalletCap}`} />
                    </div>

                    <div className="mt-3">
                        <div className="flex items-baseline justify-between text-xs text-piggy-cream/50 mb-1">
                            <span>{live ? "Live supply" : "Demo supply"}</span>
                            <span>
                                <CountUp value={Number(info.totalMinted)} format={(n) => n.toFixed(0)} />
                                {" / "}{info.maxSupply.toString()}
                            </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-piggy-pink-400 to-piggy-gold-500 transition-all duration-700"
                                style={{
                                    width: `${Math.min(100, (Number(info.totalMinted) / Number(info.maxSupply)) * 100)}%`,
                                }}
                            />
                        </div>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="space-y-4"
                >
                    {noContract && (
                        <Notice tone="warn"
                            icon={<AlertTriangle className="w-4 h-4" />}
                            title="Mint not deployed yet"
                            body="Add VITE_PIGGY_ORIGINALS_ADDRESS to your .env once the contract is deployed to testnet/mainnet."
                        />
                    )}

                    {!noContract && !info.mintActive && (
                        <Notice tone="warn"
                            icon={<AlertTriangle className="w-4 h-4" />}
                            title="Mint not yet open"
                            body="The protocol owner will activate the mint after launch."
                        />
                    )}

                    {wrongChain && (
                        <Notice tone="warn"
                            icon={<AlertTriangle className="w-4 h-4" />}
                            title="Wrong network"
                            body={`Switch to chain ID ${CHAIN_ID} (Robinhood Chain).`}
                        />
                    )}

                    <div className="glass rounded-3xl p-6">
                        <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                            Quantity
                        </div>
                        <div className="flex items-center gap-3 mb-5">
                            <button
                                onClick={() => setQuantity((q) => (q > 1n ? q - 1n : 1n))}
                                className="w-12 h-12 rounded-2xl glass hover:bg-white/[0.06] grid place-items-center"
                                aria-label="Decrease quantity"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <div className="flex-1 text-center">
                                <div className="display text-5xl font-black gradient-text">
                                    {clampedQty.toString()}
                                </div>
                                <div className="text-xs text-piggy-cream/40 mt-1">
                                    of {effectiveMax > 0n ? effectiveMax.toString() : "0"} available
                                </div>
                            </div>
                            <button
                                onClick={() => setQuantity((q) => (q < effectiveMax ? q + 1n : q))}
                                className="w-12 h-12 rounded-2xl glass hover:bg-white/[0.06] grid place-items-center"
                                aria-label="Increase quantity"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex items-baseline justify-between text-sm mb-1">
                            <span className="text-piggy-cream/60">Total</span>
                            <span className="display font-bold text-piggy-cream inline-flex items-center gap-1.5">
                                {formatEther(info.mintPrice * clampedQty)} <EthMark size={14} />
                                <span>ETH</span>
                            </span>
                        </div>
                        <div className="flex items-baseline justify-between text-xs text-piggy-cream/40 mb-5">
                            <span>Reveal anytime after mint — rarity is pre-committed</span>
                        </div>

                        {!isConnected ? (
                            <ConnectButton.Custom>
                                {({ openConnectModal }) => (
                                    <button onClick={openConnectModal} className="w-full btn-primary justify-center">
                                        Connect wallet to mint
                                    </button>
                                )}
                            </ConnectButton.Custom>
                        ) : wrongChain ? (
                            <ConnectButton.Custom>
                                {({ openChainModal }) => (
                                    <button onClick={openChainModal} className="w-full btn-primary justify-center">
                                        Switch to Robinhood Chain
                                    </button>
                                )}
                            </ConnectButton.Custom>
                        ) : (
                            <button
                                onClick={mint}
                                disabled={
                                    busy ||
                                    noContract ||
                                    !info.mintActive ||
                                    effectiveMax <= 0n
                                }
                                className="w-full btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                            >
                                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                                {justMinted && <CheckCircle2 className="w-4 h-4" />}
                                {busy ? "Confirming..." : justMinted ? "Minted!" : "Mint now"}
                            </button>
                        )}

                        {hash && (
                            <a
                                href={`${EXPLORER_TX}/${hash}`}
                                target="_blank" rel="noopener noreferrer"
                                className="mt-3 flex items-center justify-center gap-1 text-xs text-piggy-cream/50 hover:text-piggy-cream"
                            >
                                View on Explorer <ArrowUpRight className="w-3 h-3" />
                            </a>
                        )}
                    </div>

                    <RarityKey />
                </motion.div>
            </div>

            <MyCollection minted={justMinted} />
        </section>
    );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="glass rounded-2xl p-4">
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className="display text-lg font-bold text-piggy-cream inline-flex items-center gap-1.5">{value}</div>
        </div>
    );
}

function Notice({ tone, icon, title, body }: { tone: "warn"; icon: React.ReactNode; title: string; body: string }) {
    return (
        <div className={`glass-pink rounded-2xl p-4 flex gap-3 items-start ${tone === "warn" ? "" : ""}`}>
            <div className="text-piggy-pink-300 flex-shrink-0 mt-0.5">{icon}</div>
            <div>
                <div className="font-semibold text-sm text-piggy-cream mb-0.5">{title}</div>
                <div className="text-xs text-piggy-cream/70 leading-relaxed">{body}</div>
            </div>
        </div>
    );
}

function RarityKey() {
    const rows: Array<{ tier: number; count: number }> = [
        { tier: 1, count: 3000 },
        { tier: 2, count: 1250 },
        { tier: 3, count: 500 },
        { tier: 4, count: 200 },
        { tier: 5, count: 45 },
        { tier: 6, count: 5 },
    ];
    return (
        <div className="glass rounded-3xl p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-piggy-cream/50 mb-3">
                Rarity drops
            </div>
            <div className="space-y-2">
                {rows.map((r) => (
                    <div key={r.tier} className="flex items-center gap-3 text-sm">
                        <div className={`w-3 h-3 rounded-sm bg-gradient-to-br ${RARITY_COLOR[r.tier]}`} />
                        <div className="flex-1 text-piggy-cream/80">{RARITY_LABEL[r.tier]}</div>
                        <div className="text-piggy-cream/40 text-xs tabular-nums">
                            {r.count} max
                        </div>
                        <div className="text-piggy-gold-400 text-xs font-semibold tabular-nums w-12 text-right">
                            +{(RARITY_BOOST_BPS[r.tier] / 100).toFixed(2)}×
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// Sample art + tier info for the public collection preview shown when the
// visitor is disconnected or owns no Piggies yet — so everyone sees what the
// collection actually looks like before they mint.
const PREVIEW_TIERS: ReadonlyArray<{ name: string; img: string; count: number; color: string; glow: string }> = [
    { name: "Piglet",       img: "/nft-img/sample-piglet.png",       count: 3000, color: "from-pink-200 to-pink-400",     glow: "" },
    { name: "Yearling",     img: "/nft-img/sample-yearling.png",     count: 1250, color: "from-pink-300 to-pink-500",     glow: "" },
    { name: "Hog",          img: "/nft-img/sample-hog.png",          count: 500,  color: "from-pink-400 to-pink-600",     glow: "" },
    { name: "Razorback",    img: "/nft-img/sample-razorback.png",    count: 200,  color: "from-pink-500 to-pink-700",     glow: "shadow-glow" },
    { name: "Royal Sow",    img: "/nft-img/sample-royal_sow.png",    count: 45,   color: "from-piggy-gold-400 to-piggy-pink-700", glow: "shadow-gold" },
    { name: "Cosmic Piggy", img: "/nft-img/sample-cosmic_piggy.png", count: 5,    color: "from-piggy-pink-300 via-piggy-gold-400 to-piggy-violet", glow: "shadow-glow" },
];

function MyCollection({ minted }: { minted: boolean }) {
    const { isConnected } = useAccount();
    const { items, count, refetch } = useMyNFTs();
    const marketUrls = marketplaceCollectionUrls(ADDRESSES.PIGGY_ORIGINALS);

    const unrevealed = items.filter((i) => i.rarity === 0).map((i) => i.tokenId);
    const { reveal, status: revealStatus } = useReveal();
    const revealing = revealStatus === "preparing" || revealStatus === "pending" || revealStatus === "confirming";

    // Refresh the gallery as soon as a mint or reveal confirms, so the new
    // token (and the reveal button) and the revealed rarity/art appear without
    // a manual page refresh.
    useFireOnConfirm(minted, refetch);
    useFireOnConfirm(revealStatus === "confirmed", refetch);

    // Disconnected, or connected but holding nothing yet → show a public
    // collection preview instead of hiding the whole section, so a visitor
    // always sees what the Hall of Piggys looks like.
    if (!isConnected || count === 0) return <CollectionPreview />;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20"
        >
            <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
                <h2 className="display text-3xl font-black">
                    Your <span className="gradient-text">{count}</span> {count === 1 ? "Piggy" : "Piggies"}
                </h2>
                {marketUrls && (
                    <div className="flex items-center gap-2 text-xs">
                        <a
                            href={marketUrls.explorer}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full glass hover:bg-white/[0.05] text-piggy-cream/80 hover:text-piggy-cream"
                        >
                            Explorer <ArrowUpRight className="w-3 h-3" />
                        </a>
                    </div>
                )}
            </div>
            {unrevealed.length > 0 && (
                <div className="glass-pink rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-piggy-cream/80">
                        <span className="font-semibold text-piggy-cream">{unrevealed.length}</span>{" "}
                        {unrevealed.length === 1 ? "piggy is" : "piggies are"} unrevealed — reveal to lock in the rarity + LP boost.
                    </div>
                    <button
                        onClick={() => reveal(unrevealed)}
                        disabled={revealing}
                        className="btn-primary justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                        {revealing && <Loader2 className="w-4 h-4 animate-spin" />}
                        {revealStatus === "preparing"
                            ? "Preparing..."
                            : revealing
                            ? "Revealing..."
                            : revealStatus === "confirmed"
                            ? "Revealed!"
                            : `Reveal ${unrevealed.length === 1 ? "my piggy" : "all"}`}
                    </button>
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {items.map(({ tokenId, rarity }) => (
                    <PiggyCard key={tokenId.toString()} tokenId={tokenId} rarity={rarity} />
                ))}
            </div>
        </motion.div>
    );
}

function CollectionPreview() {
    const { info } = useMintInfo();
    const pct = Math.min(100, (Number(info.totalMinted) / Number(info.maxSupply)) * 100);

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-20"
        >
            <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
                <h2 className="display text-3xl font-black">
                    The <span className="gradient-text">collection</span>
                </h2>
                <span className="text-xs text-piggy-pink-300/80 tabular-nums">
                    {info.totalMinted.toString()} / {info.maxSupply.toString()} minted
                </span>
            </div>
            <p className="text-sm text-piggy-cream/50 mb-5">
                Your minted Piggies appear here — connect your wallet.
            </p>

            <div className="h-2 w-full rounded-full bg-white/[0.05] overflow-hidden mb-8">
                <div
                    className="h-full bg-gradient-to-r from-piggy-pink-400 to-piggy-gold-500 transition-all duration-700"
                    style={{ width: `${pct}%` }}
                />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {PREVIEW_TIERS.map((t) => (
                    <div key={t.name} className={`glass rounded-2xl p-4 ${t.glow}`}>
                        <div className={`aspect-square rounded-xl overflow-hidden mb-3 bg-gradient-to-br ${t.color} shadow-inner`}>
                            <img
                                src={t.img}
                                alt={`${t.name} sample`}
                                className="w-full h-full object-cover"
                                loading="lazy"
                                draggable={false}
                            />
                        </div>
                        <div className="display text-sm font-bold text-piggy-cream">{t.name}</div>
                        <div className="text-xs text-piggy-cream/40 tabular-nums">{t.count} max</div>
                    </div>
                ))}
            </div>
        </motion.div>
    );
}

function PiggyCard({ tokenId, rarity }: { tokenId: bigint; rarity: number }) {
    // While unrevealed (rarity === 0) we show the brand placeholder.
    // After reveal, we point at the per-token image URL — the same file the
    // metadata JSON references, served from the pinned IPFS collection by the
    // nft-img edge function. A token's FIRST view is a cold IPFS fetch that can
    // be slow, so retry a couple times (cache-busted) before falling back to
    // the placeholder — otherwise a freshly-minted piggy shows no art until
    // someone else warms the edge cache.
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    const isRevealed = rarity > 0;
    const base = `/nft-img/${tokenId.toString()}.png`;
    const imgSrc = !isRevealed || failed
        ? "/piggy.png"
        : attempt === 0 ? base : `${base}?r=${attempt}`;
    const onImgError = () => {
        if (attempt < 3) setTimeout(() => setAttempt((a) => a + 1), 1200);
        else setFailed(true);
    };

    return (
        <div className="glass rounded-2xl p-4">
            <div className={`aspect-square rounded-xl overflow-hidden mb-3 ${
                isRevealed ? "bg-black/30" : `bg-gradient-to-br ${RARITY_COLOR[rarity]} grid place-items-center`
            }`}>
                {isRevealed ? (
                    <img
                        src={imgSrc}
                        alt={`Safe Piggy #${tokenId.toString()}`}
                        onError={onImgError}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                    />
                ) : (
                    <PiggyMark size={70} hueRotate={Number(tokenId) % 360} />
                )}
            </div>
            <div className="text-xs text-piggy-cream/40">#{tokenId.toString()}</div>
            <div className="display text-sm font-bold text-piggy-cream">
                {RARITY_LABEL[rarity]}
            </div>
        </div>
    );
}
